/**
 * Chat sync hooks — TanStack Query wrappers around the
 * ``chatManagement`` adapter + the unified ``SyncStore``.
 *
 * Public surface (used by ``ChatSyncFiltersSheet``, ``$capability``,
 * ``ConnectedAccountRow``):
 *
 *   - ``useChatTeams``         — Teams: list of teams + channels
 *   - ``useChatSpaces``        — Google Chat: list of spaces grouped by type
 *   - ``useChatSyncStatus``    — single-account status query (5s poll)
 *   - ``useChatSyncTrigger``   — mutation: POST sync-with-filters
 *   - ``useChatForceSync``     — mutation: GET force-sync (max_conversations)
 *   - ``useChatSyncCancel``    — mutation: POST cancel-sync
 *   - ``useChatPreviewSync``   — preview lookup (estimated_conversations)
 */

import {
  type ChatPreviewSyncResponse,
  type ChatProvider,
  type ChatSyncFilters,
  type ChatSyncStatus,
  type ListSpacesResponse,
  type ListTeamsResponse,
  cancelChatSync,
  forceChatSync,
  getChatSyncStatus,
  listChatSpaces,
  listChatTeams,
  previewChatSync,
  syncChatWithFilters,
} from "@ando/core";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useLingui } from "@lingui/react/macro";

import { useChatUserId } from "@/features/chat/hooks/useChatSession";
import { useSyncStore } from "@/features/sync/state/syncStore";

// ──────────────────────────────────────────────────────────────────────
// Query keys
// ──────────────────────────────────────────────────────────────────────

const chatQueryKeys = {
  teams: (userId: string) => ["chatManagement", "teams", userId] as const,
  spaces: (userId: string) => ["chatManagement", "spaces", userId] as const,
  status: (userId: string, provider: ChatProvider, connKey?: string) =>
    ["chatManagement", "status", userId, provider, connKey ?? ""] as const,
} as const;

const PRE_SYNC_STALE_MS = 5 * 60_000;
const STATUS_POLL_MS = 5_000;

// ──────────────────────────────────────────────────────────────────────
// Read hooks
// ──────────────────────────────────────────────────────────────────────

/** Teams (Microsoft) — list of joined teams + channels. */
export function useChatTeams(enabled = true) {
  const userId = useChatUserId();
  return useQuery<ListTeamsResponse>({
    queryKey: chatQueryKeys.teams(userId ?? ""),
    queryFn: () => listChatTeams(userId as string),
    enabled: enabled && !!userId,
    staleTime: PRE_SYNC_STALE_MS,
  });
}

/** Google Chat — list of spaces grouped by type. */
export function useChatSpaces(enabled = true) {
  const userId = useChatUserId();
  return useQuery<ListSpacesResponse>({
    queryKey: chatQueryKeys.spaces(userId ?? ""),
    queryFn: () => listChatSpaces(userId as string),
    enabled: enabled && !!userId,
    staleTime: PRE_SYNC_STALE_MS,
  });
}

export function useChatSyncStatus(
  provider: ChatProvider,
  connectionKey?: string,
): ChatSyncStatus | null {
  const userId = useChatUserId();
  const { data } = useQuery({
    queryKey: chatQueryKeys.status(userId ?? "", provider, connectionKey),
    queryFn: () =>
      getChatSyncStatus(userId as string, provider, connectionKey),
    enabled: !!userId,
    refetchInterval: STATUS_POLL_MS,
  });
  return data ?? null;
}

// ──────────────────────────────────────────────────────────────────────
// Mutations
// ──────────────────────────────────────────────────────────────────────

export interface ChatSyncTriggerVars {
  readonly provider: ChatProvider;
  readonly filters: ChatSyncFilters;
  /** Slack multi-workspace key (optional elsewhere). */
  readonly connectionKey?: string;
}

/**
 * Mutation — POST ``/chat-management/sync-with-filters`` + track the
 * resulting job in the unified ``SyncStore``.
 */
export function useChatSyncTrigger() {
  const { t } = useLingui();
  const userId = useChatUserId();
  const trackJob = useSyncStore((s) => s.trackJob);
  return useMutation({
    mutationFn: async (vars: ChatSyncTriggerVars) => {
      if (!userId) throw new Error(t`Sessione utente assente`);
      const res = await syncChatWithFilters({
        user_id: userId,
        provider: vars.provider,
        filters: vars.filters,
        connection_key: vars.connectionKey,
      });
      if (res.job_id) {
        trackJob(res.job_id, "chat", {
          user_id: userId,
          provider: vars.provider,
          connection_key: vars.connectionKey,
        });
      }
      return res;
    },
  });
}

export interface ChatForceSyncVars {
  readonly provider: ChatProvider;
  readonly maxConversations?: number;
}

/** Mutation — GET ``/chat-management/force-sync`` (full re-sync default 500 conv). */
export function useChatForceSync() {
  const { t } = useLingui();
  const userId = useChatUserId();
  const trackJob = useSyncStore((s) => s.trackJob);
  return useMutation({
    mutationFn: async (vars: ChatForceSyncVars) => {
      if (!userId) throw new Error(t`Sessione utente assente`);
      const res = await forceChatSync(
        userId,
        vars.provider,
        vars.maxConversations ?? 500,
      );
      if (res.job_id) {
        trackJob(res.job_id, "chat", {
          user_id: userId,
          provider: vars.provider,
        });
      }
      return res;
    },
  });
}

/** Mutation — cancel an in-flight chat sync job. */
export function useChatSyncCancel() {
  const { t } = useLingui();
  const userId = useChatUserId();
  const cancelInStore = useSyncStore((s) => s.cancelJob);
  return useMutation({
    mutationFn: async (jobId: string) => {
      if (!userId) throw new Error(t`Sessione utente assente`);
      void cancelInStore(jobId);
      return cancelChatSync(jobId, userId);
    },
  });
}

export interface ChatPreviewVars {
  readonly provider: ChatProvider;
  readonly filters: ChatSyncFilters;
  readonly connectionKey?: string;
}

export function useChatPreviewSync() {
  const { t } = useLingui();
  const userId = useChatUserId();
  return useMutation({
    mutationFn: async (vars: ChatPreviewVars): Promise<ChatPreviewSyncResponse> => {
      if (!userId) throw new Error(t`Sessione utente assente`);
      return previewChatSync({
        user_id: userId,
        provider: vars.provider,
        filters: vars.filters,
        connection_key: vars.connectionKey,
      });
    },
  });
}

// Re-export commonly used types so callers don't need a second import.
export type {
  ChatProvider,
  ChatSyncFilters,
  ChatSyncStatus,
  ListSpacesResponse,
  ListTeamsResponse,
};
